import { useMemo } from 'react';

import { useNavigate, useParams } from 'react-router-dom';

import { Chip } from '@appcharge/shared-ui';
import { CellContext, type ColumnDef } from '@tanstack/react-table';
import useFeatureFlags from 'api/useFeatureFlags';
import { EFeatureFlag } from 'constants/enums';
import {
  appearanceUtils,
  statusMapping,
  statusUtils
} from 'utils/scheduleUtils';

import ActionCell from 'components/DataTableComponent/components/ActionCell';

import type { ProgressBarRowData } from '../../ProgressBars.types';

interface IUseProgressBarsColumnsProps {
  handleToggleActiveStatus: (offerId: string, active: boolean) => void;
  canUpdateOfferStatus: boolean;
  isManageEnabled: boolean;
  setSelectedOffer: (offerId: string) => void;
  onOpenCheats: (offerId: string) => void;
}

interface IUseProgressBarsColumnsReturn {
  columns: ColumnDef<ProgressBarRowData, unknown>[];
}

/**
 * Hook that provides column definitions for the Progress Bars table.
 *
 * @example
 * const { columns } = useProgressBarsColumns({
 *   handleToggleActiveStatus,
 *   canUpdateOfferStatus,
 *   isManageEnabled,
 *   setSelectedOffer,
 *   onOpenCheats
 * });
 */
export const useProgressBarsColumns = ({
  handleToggleActiveStatus,
  canUpdateOfferStatus,
  isManageEnabled,
  setSelectedOffer,
  onOpenCheats
}: IUseProgressBarsColumnsProps): IUseProgressBarsColumnsReturn => {
  const navigate = useNavigate();
  const { publisherId } = useParams();
  const { featureFlags } = useFeatureFlags({ publisherId });
  const hasFeatureFlagCheats = !!featureFlags?.[EFeatureFlag.DASHBOARD_PROGRESS_BAR_CHEATS];

  const columns = useMemo((): ColumnDef<ProgressBarRowData, unknown>[] => [
    {
      accessorKey: 'publisherOfferId',
      header: 'External ID',
      cell: (cell: CellContext<ProgressBarRowData, unknown>) => cell.getValue()
    },
    {
      accessorKey: 'name',
      header: 'Name',
      cell: (cell: CellContext<ProgressBarRowData, unknown>) => cell.getValue()
    },
    {
      accessorKey: 'offerUi.name',
      header: 'Design Name',
      cell: (cell: CellContext<ProgressBarRowData, unknown>) => (
        <span className="truncate">{cell.row.original?.offerUi?.name}</span>
      )
    },
    {
      accessorKey: 'missions',
      header: 'Missions',
      cell: (cell: CellContext<ProgressBarRowData, unknown>) =>
        cell.row.original.productsSequence?.length || 0
    },
    {
      accessorKey: 'active',
      header: 'Status',
      cell: (cell: CellContext<ProgressBarRowData, unknown>) => {
        const isActive = cell.getValue() as boolean;
        const status = statusUtils.getOfferStatus(cell.row.original, isActive);

        return (
          <Chip variant="flat" status={statusMapping[status]}>
            {status}
          </Chip>
        );
      }
    },
    {
      accessorKey: 'nextAppearance',
      header: 'Next Appearance',
      cell: (cell: CellContext<ProgressBarRowData, unknown>) => {
        const schedule = cell.row.original?.schedule;
        if (schedule?.permanent) return '-';
        return appearanceUtils.getNextUpcomingTime(schedule?.timeFrames) || '-';
      }
    },
    {
      accessorKey: 'actions',
      header: '',
      maxSize: 60,
      minSize: 60,
      size: 60,
      cell: (cell: CellContext<ProgressBarRowData, unknown>) => {
        const { original } = cell.row;
        return (
          <div className="max-w-[60px] w-[60px]">
            <ActionCell
              rowData={original}
              actions={[
                {
                  label: original.active ? 'Deactivate' : 'Activate',
                  handler: () =>
                    handleToggleActiveStatus(original.offerId, original.active),
                  disabled: !canUpdateOfferStatus
                },
                {
                  label: 'Edit',
                  handler: () => navigate(`./form/${original.offerId}`),
                  disabled: !isManageEnabled
                },
                {
                  label: 'Duplicate',
                  handler: () => navigate(`./dup/${original.offerId}`),
                  disabled: !isManageEnabled
                },
                {
                  label: 'Delete',
                  handler: () => {
                    setSelectedOffer(original.offerId);
                  },
                  disabled: !isManageEnabled
                },
                ...(hasFeatureFlagCheats
                  ? [
                      {
                        label: 'Cheats',
                        handler: () => onOpenCheats(original.offerId),
                        disabled: !isManageEnabled
                      }
                    ]
                  : [])
              ]}
            />
          </div>
        );
      }
    }
  ], [handleToggleActiveStatus, canUpdateOfferStatus, isManageEnabled, hasFeatureFlagCheats, setSelectedOffer, onOpenCheats, navigate]);

  return { columns };
};
